/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from "@tanstack/react-query";
import { History, Loader2, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { DraftSnapshot, DraftSnapshotDetail, ReportVersion, ReportVersionDetail, VersionDiff } from "../types";
import { Badge, Button, EmptyState, Tabs } from "../ds";
import { isBarWidget, type SPage } from "./model";

/** The slice of ownerApi / adminApi this panel needs (both implement it). */
export interface HistoryApi {
  listVersions: (id: string) => Promise<ReportVersion[]>;
  listDraftSnapshots: (id: string) => Promise<DraftSnapshot[]>;
  getVersionDetail: (id: string, versionId: string) => Promise<ReportVersionDetail>;
  getVersionDiff: (id: string, versionId: string, against?: string) => Promise<VersionDiff>;
  getDraftSnapshotDetail: (id: string, snapshotId: string) => Promise<DraftSnapshotDetail>;
  getDraftSnapshotDiff: (id: string, snapshotId: string, against?: string) => Promise<VersionDiff>;
}

type Source = "published" | "snapshots";
const KIND: Record<string, string> = { auto: "Autosave", manual: "Saved version", before_restore: "Before a restore" };
const when = (iso: string) => new Date(iso).toLocaleString([], { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

/* ---------------------------------------------------------------------------------------------
 * Read-only visual preview through the renderer contract (docs/V3_0_RENDERER_CONTRACT.md, built by
 * the renderer workstream): the iframe loads preview.html and is driven by postMessage
 *   {type:"mrp:loadDefinition", definition, dataMode}   -> draw this definition instead of the saved one
 *   {type:"mrp:highlight", widgetIds:{added,changed,removed}} -> outline what changed
 * Until the renderer answers (older preview.html) the panel falls back to a layout outline drawn here, so
 * the History drawer works either way. ("mrp:selectPage" is the existing page switch message.)
 * ------------------------------------------------------------------------------------------- */
export function VersionPreview({ reportId, definition, diff, theme = "dark" }: { reportId: string; definition: Record<string, any>; diff?: VersionDiff | null; theme?: "dark" | "light" }) {
  const pages: SPage[] = useMemo(() => ((definition?.pages as SPage[]) ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [definition]);
  const [pageId, setPageId] = useState<string>("");
  const [dataMode, setDataMode] = useState<"live" | "layout">("live");
  const [view, setView] = useState<"visual" | "outline">("visual");
  const [answered, setAnswered] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);
  const page = pages.find((p) => p.id === pageId) ?? pages.find((p) => p.id === definition?.defaultPageId) ?? pages[0];

  useEffect(() => setPageId(""), [definition]);
  useEffect(() => setAnswered(false), [definition, dataMode]);

  const post = (msg: Record<string, unknown>) => frame.current?.contentWindow?.postMessage(msg, window.location.origin);
  const push = () => {
    post({ type: "mrp:loadDefinition", definition, dataMode: dataMode === "live" ? "published" : "none", pageId: page?.id });
    if (diff) post({ type: "mrp:highlight", widgetIds: diff.widgetIds });
    else post({ type: "mrp:highlight", widgetIds: null });
  };
  useEffect(() => {
    const h = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== frame.current?.contentWindow) return;
      const t = (e.data as { type?: string })?.type;
      if (t === "mrp:ready") push();
      if (t === "mrp:rendered") setAnswered(true);
    };
    window.addEventListener("message", h);
    return () => window.removeEventListener("message", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition, diff, page?.id, dataMode]);
  useEffect(() => {
    if (view === "visual") push();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition, diff, page?.id, dataMode, view]);
  // No answer from the renderer after a few seconds: show the outline instead of an empty frame.
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    setTimedOut(false);
    if (view !== "visual") return;
    const t = window.setTimeout(() => setTimedOut(true), 6000);
    return () => window.clearTimeout(t);
  }, [definition, view, dataMode]);
  const showOutline = view === "outline" || (timedOut && !answered);

  const ids = diff?.widgetIds;
  const cv = (page?.canvas ?? { width: 1848, height: 944, columns: 24, rowHeight: 20, gap: 8 }) as any;
  const colW = (cv.width - cv.gap * (cv.columns - 1)) / cv.columns;

  if (!page) return <div className="ds-small ds-muted">This version has no pages.</div>;
  return (
    <div className="st-hist-prev">
      <div className="st-hist-prev__bar">
        {pages.length > 1 ? (
          <select className="ds-select ds-select--sm" value={page.id} onChange={(e) => setPageId(e.target.value)} aria-label="Page">
            {pages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        ) : <strong className="ds-small">{page.name}</strong>}
        <span className="ds-grow" />
        <select className="ds-select ds-select--sm" value={dataMode} onChange={(e) => setDataMode(e.target.value as any)} aria-label="Data" title="Live data shows today's numbers; layout only shows the design with placeholder data.">
          <option value="live">Today's data</option>
          <option value="layout">Layout only</option>
        </select>
        <select className="ds-select ds-select--sm" value={showOutline ? "outline" : "visual"} onChange={(e) => setView(e.target.value as any)} aria-label="View">
          <option value="visual">Picture</option>
          <option value="outline">Outline</option>
        </select>
      </div>
      <div className="st-hist-prev__stage" style={{ aspectRatio: `${cv.width} / ${cv.height}` }}>
        {showOutline ? (
          <svg viewBox={`0 0 ${cv.width} ${cv.height}`} width="100%" height="100%" role="img" aria-label={`Layout of page ${page.name}`} preserveAspectRatio="xMidYMid meet">
            <rect width={cv.width} height={cv.height} fill={theme === "dark" ? "#0a1628" : "#f8fafc"} />
            {page.widgets.filter((w) => !isBarWidget(w)).map((w) => {
              const l = w.layout;
              const x = l.x * (colW + cv.gap), y = l.y * (cv.rowHeight + cv.gap);
              const wd = l.w * colW + (l.w - 1) * cv.gap, ht = l.h * cv.rowHeight + (l.h - 1) * cv.gap;
              const st = ids?.added.includes(w.id) ? "added" : ids?.changed.includes(w.id) ? "changed" : "same";
              const col = st === "added" ? "#16a34a" : st === "changed" ? "#d97706" : "#64748b";
              return (
                <g key={w.id}>
                  <rect x={x} y={y} width={wd} height={ht} rx={8} fill={col} fillOpacity={st === "same" ? 0.18 : 0.32} stroke={col} strokeWidth={st === "same" ? 2 : 5} />
                  <text x={x + 14} y={y + 34} fontSize={26} fill={theme === "dark" ? "#e2e8f0" : "#0f172a"}>{(w.title || w.type).slice(0, 28)}</text>
                  <text x={x + 14} y={y + 64} fontSize={20} fill={col}>{w.type}{st === "same" ? "" : ` - ${st}`}</text>
                </g>
              );
            })}
          </svg>
        ) : (
          <iframe ref={frame} title="Version preview (read only)" className="st-hist-prev__frame" src={`/tv-preview/preview.html?reportId=${encodeURIComponent(reportId)}&mode=published&embed=1&await=definition&adaptive=1&fit=fixed&theme=${theme}`} onLoad={push} />
        )}
        {!showOutline && !answered ? <div className="st-hist-prev__wait"><Loader2 size={15} className="ds-spin" /> Drawing this version...</div> : null}
      </div>
      {timedOut && !answered && view === "visual" ? <div className="ds-small ds-faint">The picture is not available in this build, so the outline is shown.</div> : null}
      {ids ? (
        <div className="st-hist-legend ds-small">
          <i style={{ background: "#16a34a" }} /> added <i style={{ background: "#d97706" }} /> changed
          {ids.removed.length ? <span className="ds-faint"> · {ids.removed.length} removed (not on this version)</span> : null}
        </div>
      ) : null}
    </div>
  );
}

/** "Changes vs previous version" as plain sentences. */
export function DiffList({ diff }: { diff: VersionDiff }) {
  const w = diff.widgets;
  return (
    <div className="st-hist-diff">
      <div className="ds-row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
        <Badge tone="success">{w.added.length} added</Badge>
        <Badge tone="warning">{w.changed.length} changed</Badge>
        <Badge tone="danger">{w.removed.length} removed</Badge>
      </div>
      {diff.lines.length === 0 ? <div className="ds-small ds-muted">No differences.</div> : (
        <ul className="st-hist-lines">
          {diff.lines.map((l, i) => (
            <li key={i} className={/ added/.test(l) ? "is-add" : / removed/.test(l) ? "is-del" : ""}>{l}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Reusable version history: published versions + draft snapshots, each with a read-only picture and a
 *  "changes vs previous" list. `onRestore` receives the chosen definition (Studio commits it as ONE undo step;
 *  elsewhere save it as the draft). */
export function VersionHistoryPanel({
  api, reportId, queryScope, onRestore, canRestore = true, theme,
}: {
  api: HistoryApi;
  reportId: string;
  /** Distinguishes cache entries (e.g. "owner" / "admin"). */
  queryScope: string;
  onRestore?: (definition: Record<string, any>, label: string) => void;
  canRestore?: boolean;
  theme?: "dark" | "light";
}) {
  const [source, setSource] = useState<Source>("published");
  const [selected, setSelected] = useState<string | null>(null);
  const [against, setAgainst] = useState("previous");
  const versions = useQuery({ queryKey: ["hist-versions", queryScope, reportId], queryFn: () => api.listVersions(reportId) });
  const snaps = useQuery({ queryKey: ["hist-snaps", queryScope, reportId], queryFn: () => api.listDraftSnapshots(reportId) });
  const list = source === "published" ? versions : snaps;
  const items = (list.data ?? []) as Array<ReportVersion | DraftSnapshot>;

  // Select the newest entry when switching lists.
  useEffect(() => {
    setSelected(items[0]?.id ?? null);
    setAgainst("previous");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, list.data]);

  const detail = useQuery<{ definition_json: Record<string, unknown> }>({
    queryKey: ["hist-detail", queryScope, reportId, source, selected],
    enabled: !!selected,
    queryFn: () => (source === "published" ? api.getVersionDetail(reportId, selected!) : api.getDraftSnapshotDetail(reportId, selected!)),
  });
  const diff = useQuery({
    queryKey: ["hist-diff", queryScope, reportId, source, selected, against],
    enabled: !!selected,
    queryFn: () => (source === "published" ? api.getVersionDiff(reportId, selected!, against) : api.getDraftSnapshotDiff(reportId, selected!, against)),
  });
  const sel = items.find((i) => i.id === selected);
  const selLabel = sel ? ("version_number" in sel ? `version ${sel.version_number}` : (sel.label || KIND[sel.kind] || "snapshot")) : "";

  return (
    <div className="st-hist">
      <Tabs<Source> value={source} onChange={setSource} tabs={[{ value: "published", label: "Published versions", count: versions.data?.length }, { value: "snapshots", label: "Saved drafts", count: snaps.data?.length }]} />
      <div className="st-hist__grid">
        <div className="st-hist__list" role="listbox" aria-label="History">
          {list.isLoading ? <div className="ds-small ds-muted"><Loader2 size={14} className="ds-spin" /> Loading...</div> : null}
          {!list.isLoading && items.length === 0 ? (
            <EmptyState icon={History} title={source === "published" ? "Nothing published yet" : "No saved drafts yet"} description={source === "published" ? "Each time you publish, a numbered version is kept here so you can look back." : "The designer keeps automatic copies of your draft while you work."} />
          ) : null}
          {items.map((it) => {
            const isV = "version_number" in it;
            return (
              <button key={it.id} type="button" role="option" aria-selected={it.id === selected} className={`st-hist__item${it.id === selected ? " is-on" : ""}`} onClick={() => { setSelected(it.id); setAgainst("previous"); }}>
                <strong>{isV ? `Version ${(it as ReportVersion).version_number}` : (it as DraftSnapshot).label || KIND[(it as DraftSnapshot).kind] || "Snapshot"}</strong>
                <span className="ds-small ds-faint">{when(isV ? (it as ReportVersion).created_at : (it as DraftSnapshot).captured_at)}</span>
                <span className="ds-small ds-muted">{isV ? (it as ReportVersion).change_summary || "" : `${(it as DraftSnapshot).page_count} pages, ${(it as DraftSnapshot).widget_count} visuals`}</span>
              </button>
            );
          })}
        </div>
        <div className="st-hist__detail">
          {!selected ? <div className="ds-small ds-muted">Pick an entry on the left to see how the report looked and what changed.</div> : (
            <>
              <div className="ds-row" style={{ gap: 8, alignItems: "center" }}>
                <strong className="ds-grow">{selLabel}</strong>
                {canRestore && onRestore && detail.data ? (
                  <Button size="sm" variant="primary" icon={RotateCcw} title="Replace your current draft with this one. You can undo it." onClick={() => onRestore(detail.data!.definition_json, selLabel)}>Restore as draft</Button>
                ) : null}
              </div>
              {detail.isLoading ? <div className="ds-small ds-muted"><Loader2 size={14} className="ds-spin" /> Loading this version...</div> : null}
              {detail.isError ? <div className="ds-error-text">Could not load this version.</div> : null}
              {detail.data ? <VersionPreview reportId={reportId} definition={detail.data.definition_json as any} diff={diff.data ?? null} theme={theme} /> : null}
              <div className="st-section__label" style={{ marginTop: 12 }}>
                Changes vs {source === "published" && against !== "previous" ? "version " + (versions.data?.find((v) => v.id === against)?.version_number ?? "?") : "previous " + (source === "published" ? "version" : "save")}
                {source === "published" && (versions.data?.length ?? 0) > 2 ? (
                  <select className="ds-select ds-select--sm" style={{ marginLeft: 8 }} value={against} onChange={(e) => setAgainst(e.target.value)} aria-label="Compare with">
                    <option value="previous">Previous version</option>
                    {versions.data?.filter((v) => v.id !== selected).map((v) => <option key={v.id} value={v.id}>Version {v.version_number}</option>)}
                  </select>
                ) : null}
              </div>
              {diff.isLoading ? <div className="ds-small ds-muted">Comparing...</div> : null}
              {diff.data ? <DiffList diff={diff.data} /> : null}
              {diff.data && !diff.data.againstVersionId && !diff.data.againstSnapshotId && source === "published" ? <div className="ds-small ds-faint">This is the first version, so everything counts as new.</div> : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
